/**
 * JSON-LD helpers.
 *
 * Pages return an array of graph nodes via
 * `documentProps.jsonLdGraph`. The renderer emits one
 * `<script type="application/ld+json">` per node. This is functionally
 * equivalent to a `@graph` wrapper but keeps each node independently
 * validatable (the Google Rich Results Test consumes per-script).
 */

import * as React from "react";

export { buildOrganization } from "./organization.js";
export { buildWebSite } from "./website.js";
export { buildBreadcrumbList } from "./breadcrumb.js";
export type { BreadcrumbCrumb } from "./breadcrumb.js";
export { buildFaqPage } from "./faqPage.js";
export type { FaqEntry } from "./faqPage.js";
export { buildNewsArticle } from "./newsArticle.js";
export { buildArticle } from "./article.js";
export { buildMedicalWebPage } from "./medicalWebPage.js";
export { buildMedicalCondition } from "./medicalCondition.js";
export { buildDataset } from "./dataset.js";
export type { DatasetDistribution } from "./dataset.js";
export { buildEvent } from "./event.js";
export { buildPlace } from "./place.js";
export { buildCollectionPage } from "./collectionPage.js";
export { buildItemList } from "./itemList.js";
export type { ItemListEntry } from "./itemList.js";
export { buildWebPage } from "./webPage.js";

/**
 * Escape a string for safe inclusion inside a `<script>` tag. Replaces
 * `</` with `<\/` so a malicious value cannot prematurely close the
 * script element. Backslashes and other JSON metacharacters are
 * already escaped by `JSON.stringify`.
 */
export function escapeJsonLdForScript(json: string): string {
  return json.replace(/<\/(script)/gi, "<\\/$1");
}

/**
 * Render a single JSON-LD node as a React element. Safe to call from
 * SSR — `renderToString` will inline the JSON without re-escaping.
 */
export function JsonLdScript({ data }: { data: object }): JSX.Element {
  return React.createElement("script", {
    type: "application/ld+json",
    dangerouslySetInnerHTML: {
      __html: escapeJsonLdForScript(JSON.stringify(data)),
    },
  });
}

/**
 * Serialise a list of JSON-LD nodes to an HTML string of
 * `<script type="application/ld+json">…</script>` tags. Used by the
 * SSR renderer when stitching the final `<head>`.
 */
export function jsonLdGraphToHtml(graph: object[] | undefined): string {
  if (!graph || graph.length === 0) return "";
  return graph
    .map(
      (node) =>
        `<script type="application/ld+json">${escapeJsonLdForScript(JSON.stringify(node))}</script>`,
    )
    .join("\n    ");
}
